/** Email audience: subscribers (+import/export), suppressions, segments (+live count), sign-up forms. */
import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Checkbox, DatePicker, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Segmented, Select, Space, Switch, Table, Tag, Typography, Upload, message,
} from 'antd';
import { DeleteOutlined, DownloadOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from '../../../shared/i18n';
import TagsInput from '../../../shared/components/TagsInput';
import { directUpload } from './api';
import { fmtDate, listOf, totalOf, useSite } from './common';
import { mktError } from './MarketingCommon';

const { Text } = Typography;
const SUB_STATUS = { subscribed: 'green', pending: 'gold', unsubscribed: 'default' };

const ImportModal = ({ open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [file, setFile] = useState(null);
    const [attest, setAttest] = useState(false);
    const [text, setText] = useState('');
    const [tags, setTags] = useState([]);
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    useEffect(() => { if (open) { setFile(null); setAttest(false); setText(''); setTags([]); setRes(null); } }, [open]);
    const go = async () => {
        setBusy(true);
        try {
            const { key } = await directUpload(api, file, 'import');
            const r = await api.post('/marketing/subscribers/import', { key, consent_attested: 1, consent_text: text, tags });
            setRes(r);
            onDone();
        } catch (e) { mktError(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} onCancel={onClose} title={t('Import subscribers')} destroyOnHidden
            footer={res ? <Button onClick={onClose}>{t('Close')}</Button> : undefined}
            onOk={go} okText={t('Import')} confirmLoading={busy} okButtonProps={{ disabled: !file || !attest || !text.trim() }}>
            {res ? (
                <Space direction="vertical">
                    {Object.entries(res).filter(([, v]) => typeof v === 'number').map(([k, v]) => <Text key={k}>{t(k.replace(/_/g, ' '))}: <b>{v}</b></Text>)}
                </Space>
            ) : (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Upload accept=".csv,text/csv" maxCount={1} beforeUpload={(f) => { setFile(f); return false; }} onRemove={() => setFile(null)} fileList={file ? [file] : []}>
                        <Button icon={<UploadOutlined />}>{t('Choose CSV')}</Button>
                    </Upload>
                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Columns')}: email, name, tags, country, lang, birthday</Text>
                    <TagsInput format="array" value={tags} onChange={setTags} placeholder={t('Add tags (optional)')} />
                    <Input.TextArea value={text} onChange={(e) => setText(e.target.value)} autoSize={{ minRows: 2 }} maxLength={500}
                        placeholder={t('How did these people agree to receive email? e.g. "Signed up at our store counter, 2025"')} />
                    <Checkbox checked={attest} onChange={(e) => setAttest(e.target.checked)}>{t('I confirm everyone on this list agreed to receive marketing email from us')}</Checkbox>
                </Space>
            )}
        </Modal>
    );
};

const SubscriberDrawer = ({ id, onClose, onChanged }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [s, setS] = useState(null);
    const [form] = Form.useForm();
    useEffect(() => {
        if (!id) return;
        if (id === 'new') { setS({}); form.resetFields(); return; }
        api.get(`/marketing/subscribers/${id}`).then((d) => {
            const x = d?.subscriber || d;
            setS(x);
            form.setFieldsValue({ ...x, birthday: x.birthday ? dayjs(x.birthday) : null });
        }).catch((e) => { mktError(e); onClose(); });
    }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
    const save = async () => {
        const v = await form.validateFields();
        const body = { ...v, birthday: v.birthday ? v.birthday.format('YYYY-MM-DD') : null };
        try {
            if (id === 'new') await api.post('/marketing/subscribers', { ...body, consent_attested: true });
            else await api.put(`/marketing/subscribers/${id}`, { ...body, ...(v.status === 'subscribed' && s.status !== 'subscribed' ? { consent_attested: true } : {}) });
            message.success(t('Saved'));
            onChanged();
            onClose();
        } catch (e) { mktError(e); }
    };
    const del = async () => { try { await api.del(`/marketing/subscribers/${id}`); onChanged(); onClose(); } catch (e) { mktError(e); } };
    return (
        <Drawer open={!!id} onClose={onClose} width={Math.min(460, window.innerWidth)} title={id === 'new' ? t('Add subscriber') : s?.email}
            extra={<Space>{id !== 'new' && <Popconfirm title={t('Delete?')} onConfirm={del}><Button danger icon={<DeleteOutlined />} /></Popconfirm>}<Button type="primary" onClick={save}>{t('Save')}</Button></Space>}>
            {s && (
                <Form form={form} layout="vertical" requiredMark={false}>
                    {id === 'new' && <Form.Item name="email" label={t('Email', { context: 'shop' })} rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>}
                    <Form.Item name="name" label={t('Name')}><Input /></Form.Item>
                    {id !== 'new' && (
                        <Form.Item name="status" label={t('Status')}>
                            <Select options={['subscribed', 'pending', 'unsubscribed'].map((x) => ({ value: x, label: t(x[0].toUpperCase() + x.slice(1)) }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="tags" label={t('Tags')}><TagsInput format="array" /></Form.Item>
                    <Space>
                        <Form.Item name="country" label={t('Country')}><Input maxLength={2} style={{ width: 90 }} /></Form.Item>
                        <Form.Item name="lang" label={t('Language')}><Input style={{ width: 90 }} /></Form.Item>
                        <Form.Item name="birthday" label={t('Birthday')}><DatePicker /></Form.Item>
                    </Space>
                    {id === 'new' && <Text type="secondary" style={{ fontSize: 12 }}>{t('Only add people who agreed to receive marketing email.')}</Text>}
                    {s.source && <div><Text type="secondary" style={{ fontSize: 12 }}>{t('Source')}: {s.source} · {fmtDate(s.subscribed_at || s.created_at)}</Text></div>}
                    {s.suppressed && <Alert type="warning" showIcon style={{ marginTop: 8 }} message={`${t('Suppressed')}: ${s.suppressed}`} />}
                </Form>
            )}
        </Drawer>
    );
};

export const Subscribers = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [f, setF] = useState({ status: '', q: '', page: 1 });
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [importOpen, setImportOpen] = useState(false);
    const [open, setOpen] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await api.get('/marketing/subscribers', { status: f.status || undefined, q: f.q || undefined, page: f.page, per_page: 50 });
            setData({ items: listOf(d), total: totalOf(d) });
        } catch (e) { mktError(e); } finally { setLoading(false); }
    }, [api, f]);
    useEffect(() => { load(); }, [load]);
    return (
        <>
            <Space wrap style={{ marginBottom: 12, width: '100%', justifyContent: 'space-between' }}>
                <Space wrap>
                    <Segmented value={f.status} onChange={(status) => setF({ ...f, status, page: 1 })}
                        options={[{ value: '', label: t('All') }, { value: 'subscribed', label: t('Subscribed') }, { value: 'pending', label: t('Pending') }, { value: 'unsubscribed', label: t('Unsubscribed') }]} />
                    <Input.Search allowClear placeholder={t('Search email or name')} style={{ width: 220 }} onSearch={(q) => setF({ ...f, q, page: 1 })} />
                </Space>
                <Space>
                    <Button icon={<DownloadOutlined />} onClick={() => api.download('/marketing/subscribers/export', 'subscribers.csv', { status: f.status || undefined })}>{t('Export')}</Button>
                    <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>{t('Import')}</Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen('new')}>{t('Add')}</Button>
                </Space>
            </Space>
            <Table rowKey="id" size="small" loading={loading} dataSource={data.items} scroll={{ x: 700 }}
                onRow={(s) => ({ onClick: () => setOpen(s.id), style: { cursor: 'pointer' } })}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No subscribers yet')} /> }}
                pagination={{ current: f.page, pageSize: 50, total: data.total, onChange: (page) => setF({ ...f, page }), hideOnSinglePage: true, showSizeChanger: false }}
                columns={[
                    { title: t('Email', { context: 'shop' }), dataIndex: 'email', render: (v, s) => <Space direction="vertical" size={0}><Text>{v}</Text>{s.name && <Text type="secondary" style={{ fontSize: 12 }}>{s.name}</Text>}</Space> },
                    { title: t('Status'), dataIndex: 'status', width: 130, render: (v, s) => <Space size={2}><Tag color={SUB_STATUS[v]}>{t(v ? v[0].toUpperCase() + v.slice(1) : '-')}</Tag>{s.suppressed && <Tag color="red">{t('Suppressed')}</Tag>}</Space> },
                    { title: t('Tags'), dataIndex: 'tags', render: (v) => (v || []).slice(0, 4).map((x) => <Tag key={x}>{x}</Tag>) },
                    { title: t('Source'), dataIndex: 'source', width: 120 },
                    { title: t('Added'), dataIndex: 'subscribed_at', width: 160, render: (v, s) => fmtDate(v || s.created_at) },
                ]} />
            <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
            <SubscriberDrawer id={open} onClose={() => setOpen(null)} onChanged={load} />
        </>
    );
};

export const Suppressions = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [data, setData] = useState({ items: [], total: 0 });
    const [q, setQ] = useState('');
    const [page, setPage] = useState(1);
    const [email, setEmail] = useState('');
    const load = useCallback(() => {
        api.get('/marketing/suppressions', { q: q || undefined, page, per_page: 50 }).then((d) => setData({ items: listOf(d), total: totalOf(d) })).catch(mktError);
    }, [api, q, page]);
    useEffect(() => { load(); }, [load]);
    const add = async () => {
        if (!email.trim()) return;
        try { await api.post('/marketing/suppressions', { email: email.trim() }); setEmail(''); load(); } catch (e) { mktError(e); }
    };
    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search')} style={{ width: 220 }} onSearch={(v) => { setQ(v); setPage(1); }} />
                <Space.Compact><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email@example.com" onPressEnter={add} /><Button onClick={add}>{t('Block')}</Button></Space.Compact>
            </Space>
            <Table rowKey="id" size="small" dataSource={data.items} pagination={{ current: page, pageSize: 50, total: data.total, onChange: setPage, hideOnSinglePage: true }}
                columns={[
                    { title: t('Email', { context: 'shop' }), dataIndex: 'email' },
                    { title: t('Reason'), dataIndex: 'reason', width: 140, render: (v) => <Tag color={v === 'manual' ? 'default' : 'red'}>{t(v || '-')}</Tag> },
                    { title: t('Added'), dataIndex: 'created_at', width: 160, render: (v) => fmtDate(v) },
                    { title: '', key: 'd', width: 50, render: (_, r) => <Popconfirm title={t('Allow email again?')} onConfirm={async () => { try { await api.del(`/marketing/suppressions/${r.id}`); load(); } catch (e) { mktError(e); } }}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm> },
                ]} />
        </>
    );
};

// ---------------------------------------------------------------- segments
const FIELDS = {
    orders_count: { label: 'Orders count', ops: ['gte', 'gt', 'eq', 'lt', 'lte', 'ne'], kind: 'number' },
    total_spent: { label: 'Total spent (minor units)', ops: ['gte', 'gt', 'lt', 'lte'], kind: 'number' },
    last_order_date: { label: 'Last order', ops: ['within_days', 'not_within_days', 'before', 'after'], kind: 'days' },
    product_purchased: { label: 'Bought product (ID)', ops: ['is', 'is_not'], kind: 'number' },
    collection_purchased: { label: 'Bought from collection (ID)', ops: ['is', 'is_not'], kind: 'number' },
    tag: { label: 'Tag', ops: ['has', 'has_not'], kind: 'text' },
    country: { label: 'Country', ops: ['is', 'is_not'], kind: 'text' },
    lang: { label: 'Language', ops: ['is', 'is_not'], kind: 'text' },
    accepts_marketing: { label: 'Accepts marketing', ops: ['is'], kind: 'bool' },
    source: { label: 'Source', ops: ['is', 'starts_with'], kind: 'text' },
    abandoned_checkout: { label: 'Abandoned checkout', ops: ['within_days'], kind: 'number' },
    subscribed_date: { label: 'Subscribed', ops: ['within_days', 'before', 'after'], kind: 'days' },
    engaged: { label: 'Opened or clicked', ops: ['within_days', 'not_within_days'], kind: 'number' },
};
const OPS = {
    eq: '=', ne: '≠', gt: '>', gte: '≥', lt: '<', lte: '≤', within_days: 'in the last N days', not_within_days: 'not in the last N days',
    before: 'before', after: 'after', is: 'is', is_not: 'is not', has: 'has', has_not: 'does not have', starts_with: 'starts with',
};

const RuleRow = ({ c, onChange, onRemove }) => {
    const { t } = useTranslation();
    const def = FIELDS[c.field] || FIELDS.orders_count;
    const dateOp = ['before', 'after'].includes(c.op);
    let input;
    if (def.kind === 'bool') input = <Select style={{ width: 120 }} value={c.value === true || c.value === 'true'} onChange={(value) => onChange({ value })} options={[{ value: true, label: t('Yes') }, { value: false, label: t('No') }]} />;
    else if (dateOp) input = <DatePicker value={c.value ? dayjs(c.value) : null} onChange={(d) => onChange({ value: d ? d.format('YYYY-MM-DD') : '' })} />;
    else if (def.kind === 'text') input = <Input style={{ width: 160 }} value={c.value} onChange={(e) => onChange({ value: e.target.value })} />;
    else input = <InputNumber style={{ width: 120 }} value={c.value} onChange={(value) => onChange({ value })} />;
    return (
        <Space wrap style={{ marginBottom: 6 }}>
            <Select style={{ width: 220 }} value={c.field} onChange={(field) => onChange({ field, op: FIELDS[field].ops[0], value: FIELDS[field].kind === 'bool' ? true : '' })}
                options={Object.entries(FIELDS).map(([k, v]) => ({ value: k, label: t(v.label) }))} />
            <Select style={{ width: 170 }} value={c.op} onChange={(op) => onChange({ op })} options={def.ops.map((o) => ({ value: o, label: t(OPS[o]) }))} />
            {input}
            <Button type="text" danger icon={<DeleteOutlined />} onClick={onRemove} />
        </Space>
    );
};

const SegmentModal = ({ seg, onClose, onSaved }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [name, setName] = useState('');
    const [rules, setRules] = useState({ match: 'all', conditions: [] });
    const [count, setCount] = useState(null);
    useEffect(() => {
        if (seg) { setName(seg.name || ''); setRules(seg.rules?.conditions ? { match: seg.rules.match || 'all', conditions: seg.rules.conditions } : { match: 'all', conditions: [{ field: 'orders_count', op: 'gte', value: 1 }] }); setCount(null); }
    }, [seg]);
    useEffect(() => {
        if (!seg) return undefined;
        const h = setTimeout(() => {
            api.post('/marketing/segments/preview', { rules }, { skipGlobalErrorToast: true }).then((r) => setCount(r?.count ?? null)).catch(() => setCount(null));
        }, 500);
        return () => clearTimeout(h);
    }, [rules, seg, api]);
    const save = async () => {
        try {
            if (seg.id) await api.put(`/marketing/segments/${seg.id}`, { name, rules });
            else await api.post('/marketing/segments', { name, rules });
            onSaved();
            onClose();
        } catch (e) { mktError(e); }
    };
    const setCond = (i, patch) => setRules((r) => ({ ...r, conditions: r.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) }));
    return (
        <Modal open={!!seg} onCancel={onClose} onOk={save} okText={t('Save')} title={seg?.id ? t('Edit segment') : t('New segment')} width={760} destroyOnHidden okButtonProps={{ disabled: !name.trim() }}>
            <Space direction="vertical" style={{ width: '100%' }}>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t('Segment name')} autoFocus />
                <Space>
                    <Text>{t('Match')}</Text>
                    <Segmented size="small" value={rules.match} onChange={(match) => setRules({ ...rules, match })} options={[{ value: 'all', label: t('All conditions') }, { value: 'any', label: t('Any condition') }]} />
                </Space>
                {rules.conditions.map((c, i) => <RuleRow key={i} c={c} onChange={(p) => setCond(i, p)} onRemove={() => setRules({ ...rules, conditions: rules.conditions.filter((_, j) => j !== i) })} />)}
                <Button size="small" icon={<PlusOutlined />} onClick={() => setRules({ ...rules, conditions: [...rules.conditions, { field: 'tag', op: 'has', value: '' }] })} disabled={rules.conditions.length >= 20}>{t('Add condition')}</Button>
                <Text strong>{count === null ? '—' : t('{{n}} subscribers match', { n: count })}</Text>
            </Space>
        </Modal>
    );
};

export const Segments = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [list, setList] = useState(null);
    const [edit, setEdit] = useState(null);
    const load = useCallback(() => { api.get('/marketing/segments').then((d) => setList(listOf(d))).catch((e) => { setList([]); mktError(e); }); }, [api]);
    useEffect(() => { load(); }, [load]);
    return (
        <>
            <div style={{ textAlign: 'right', marginBottom: 12 }}><Button type="primary" icon={<PlusOutlined />} onClick={() => setEdit({})}>{t('New segment')}</Button></div>
            <Table rowKey="id" size="small" loading={list === null} dataSource={list || []} pagination={false}
                onRow={(s) => ({ onClick: () => setEdit(s), style: { cursor: 'pointer' } })}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No segments yet')} /> }}
                columns={[
                    { title: t('Segment'), dataIndex: 'name' },
                    { title: t('Conditions'), key: 'c', render: (_, s) => (s.rules?.conditions || []).map((c, i) => <Tag key={i}>{t(FIELDS[c.field]?.label || c.field)} {t(OPS[c.op] || c.op)} {String(c.value)}</Tag>) },
                    { title: t('Subscribers'), dataIndex: 'count', width: 110, align: 'right' },
                    { title: '', key: 'd', width: 50, render: (_, s) => <span onClick={(e) => e.stopPropagation()}><Popconfirm title={t('Delete?')} onConfirm={async () => { try { await api.del(`/marketing/segments/${s.id}`); load(); } catch (e) { mktError(e); } }}><Button size="small" type="text" danger icon={<DeleteOutlined />} /></Popconfirm></span> },
                ]} />
            <SegmentModal seg={edit} onClose={() => setEdit(null)} onSaved={load} />
        </>
    );
};

// ---------------------------------------------------------------- forms
export const SignupForms = () => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [list, setList] = useState(null);
    const load = useCallback(() => { api.get('/marketing/forms').then((d) => setList(listOf(d))).catch((e) => { setList([]); mktError(e); }); }, [api]);
    useEffect(() => { load(); }, [load]);
    const save = async (f, patch) => {
        const next = { ...f, ...patch };
        setList((xs) => xs.map((x) => (x.form_key === f.form_key ? next : x)));
        try { await api.put(`/marketing/forms/${f.form_key}`, { subscribe: next.subscribe, consent_field: next.consent_field || '', tags: next.tags || [] }); } catch (e) { mktError(e); load(); }
    };
    return (
        <Table rowKey="form_key" size="small" loading={list === null} dataSource={list || []} pagination={false} scroll={{ x: 700 }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No forms on this site')} /> }}
            columns={[
                { title: t('Form'), key: 'n', render: (_, f) => <Space direction="vertical" size={0}><Text strong>{f.name}</Text><Text type="secondary" style={{ fontSize: 12 }}>{f.form_key}</Text></Space> },
                { title: t('Adds subscribers'), dataIndex: 'subscribe', width: 140, render: (v, f) => <Switch checked={!!v} onChange={(subscribe) => save(f, { subscribe })} /> },
                {
                    title: t('Consent checkbox'), dataIndex: 'consent_field', width: 200, render: (v, f) => (f.signup_form ? <Text type="secondary">{t('Sign-up form')}</Text> : (
                        <Select allowClear size="small" style={{ width: '100%' }} value={v || undefined} placeholder={t('None')} onChange={(consent_field) => save(f, { consent_field })}
                            options={(f.fields || []).filter((x) => ['checkbox'].includes(x.type)).map((x) => ({ value: x.name, label: x.label || x.name }))} />
                    )),
                },
                { title: t('Tags'), dataIndex: 'tags', width: 240, render: (v, f) => <TagsInput size="small" format="array" value={v || []} onChange={(tags) => save(f, { tags })} /> },
            ]} />
    );
};
